<template>
  <div ref="labelPreview"></div>
</template>

<script>
import SVG from "svg.js"
import TurretTopPreview from "./turret_top_preview"
import svgHelpers from "../../../helpers/svg_helper"
import find from "lodash/find"
import merge from "lodash/merge"
import cloneDeep from "lodash/cloneDeep"

/*
This array contains configurations for the label's revolutions. Each element
of this array corresponds to a revolution by its index. Following things can
be customized:

- Height - Revolution's height in percents.
- drawFromTopToBottom - If set true, ticks will be drawn from top to bottom.
- noyards - Tells how to display clicks that don't represent any yardage.
- quarterYards - Tells how to display clicks that represent yardage changes by 25 yards (Examples: 125, 175).
- halfYards - Tells how to display clicks that represent yardage changes by 50 yards (Example: 150).
- hundredYards - Tells how to display clicks that represent yardage changes by 100 yards (Example: 200).

noyards, quarterYards, halfYards, hundredYards options support the following values:

- False - no tick should be displayed.
- Height (Number from 0 to 1) - the click should be displayed as a tick with the specified height(in percents).
- Object - Tells how to display a click. Should have a style key with the value equal to one of these:
 - tick
 - number
 - circle
 - tick_with_number

You can also have multiple settings sets for a single revolution depending on total number of revolutions.
To do so you should define your settings sets under integer keys that refer to the number of revolutions.
*/
const DEFAULT_REVOLUTIONS_CONFIG = [{
  total_number_of_revolutions: 1,
  revolution_number: 0,
  height: 100,
  click_tick_height: false,
  quarter_yards: 30,
  half_yards: 50,
  hundred_yards: {
    style: "tick_with_number",
    tick_height: 15,
    font_size: 30,
    number_offset: 20
  }
}, {
  total_number_of_revolutions: 2,
  revolution_number: 0,
  height: 70,
  click_tick_height: false,
  quarter_yards: 50,
  half_yards: 75,
  hundred_yards: {
    style: "tick_with_number",
    tick_height: 15,
    font_size: 40,
    number_offset: 20
  }
}, {
  total_number_of_revolutions: 2,
  revolution_number: 1,
  height: 30,
  drawFromTopToBottom: true,
  click_tick_height: false,
  quarter_yards: {
    style: "circle"
  },
  half_yards: 1,
  hundred_yards: {
    style: "number",
    font_size: 70,
    number_offset: 10
  }
}]

const SAMPLE_LABEL_HEIGHT = 70 // 70% of label's height
const FONT_FAMILY = "Arial"
const ZERO_POINT_FONT_SIZE = 20 // 20% of first revolution's height
const ZERO_POINT_TICK_HIEGHT = 5

const countPercentage = (valueInPx, percentage) => {
  return valueInPx * percentage / 100
}

export default {
  props: {
    height: Number,
    diameter: Number,
    numberOfRevolutions: Number,
    clicksPerRotation: Number,
    direction: String,
    calculationResults: Array,
    fixTurretPreview: {
      type: Boolean,
      default: false
    },
    linesType: {
      type: String,
      default: "yardage"
    },
    snapToClicks: {
      type: Boolean,
      default: false
    },
    initialSizeMode: {
      type: String,
      default: "responsive"
    },
    showExampleSign: {
      type: Boolean,
      default: true
    },
    revolutionsConfiguration: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      sizeMode: this.initialSizeMode,
      drawingMode: "preview"
    }
  },
  computed: {
    widthInPx() { return svgHelpers.inchesToPx(this.diameter * Math.PI) },
    halfWidthPx() { return this.widthInPx / 2 },
    heightInPx() { return svgHelpers.inchesToPx(this.height) },
    itemsFillColor() { return svgHelpers.itemsFillColor(this.drawingMode) },
    distanceBetweenClicks() { return this.widthInPx / this.clicksPerRotation },
    isYardageLinesMode() { return this.linesType === "yardage" },
    zeroPointX() {
      const zeroPointX = this.isYardageLinesMode ? this.horizontalCoordinateForYardageResult(0, 0) : 0
      return zeroPointX + this.halfWidthPx
    },
  },
  methods: {
    revolutionConfig(index) {
      const configCriteria = {total_number_of_revolutions: this.numberOfRevolutions, revolution_number: index}
      const defaultConfig = cloneDeep(find(DEFAULT_REVOLUTIONS_CONFIG, configCriteria))
      const customConfig = find(this.revolutionsConfiguration, configCriteria)

      return merge(defaultConfig, customConfig)
    },
    resolveRevolutionHeight(index) {
      return this.revolutionConfig(index).height
    },
    revolutionY(index) {
      let topY = this.heightInPx
      const revolutionHeight = countPercentage(this.heightInPx, this.resolveRevolutionHeight(index))

      if (index !== 0) {
        const [prevRevolutionTopY, _prevRevolutionBottomY] = this.revolutionY(index - 1)
        topY = prevRevolutionTopY
      }

      return [topY - revolutionHeight, topY]
    },
    revolutionYCenter(index) {
      const [revolutionTopY, revolutionBottomY] = this.revolutionY(index)
      return revolutionBottomY - (revolutionBottomY - revolutionTopY) * 0.5
    },
    revolutionHeight(index) {
      const [revolutionTopY, revolutionBottomY] = this.revolutionY(index)
      return revolutionBottomY - revolutionTopY
    },
    draw(drawingMode = "preview", sizeMode = null) {
      if (!sizeMode) sizeMode = this.sizeMode
      this.drawingMode = drawingMode

      svgHelpers.setViewboxByMode(this.svg, drawingMode, this.widthInPx, this.heightInPx)
      svgHelpers.resizeSVGBySizeMode(this.svg, sizeMode, this.widthInPx, this.heightInPx)
      svgHelpers.drawBackground(this.svg, drawingMode, sizeMode, this.widthInPx, this.heightInPx)

      if (this.calculationResults.length) {
        this.drawZeroPoint()
      }

      this.drawClicks()
      this.drawYardageRelatedItems()

      if (this.showExampleSign) {
        this.drawSampleLabel()
      }
    },
    redraw(mode = "preview", sizeMode = null) {
      this.svg.clear()
      this.draw(mode, sizeMode)
    },
    drawSampleLabel() {
      this.svg
        .plain("PREVIEW")
        .font({anchor: "middle", family: FONT_FAMILY, size: countPercentage(this.heightInPx, SAMPLE_LABEL_HEIGHT)})
        .attr({fill: "var(--white-mask)", "dominant-baseline": "central", "letter-spacing": 10})
        .move("50%", "50%")
    },
    drawClicks() {
      for (let revolutionIndex = 0; revolutionIndex < this.numberOfRevolutions; revolutionIndex++) {
        const revolutionConfig = this.revolutionConfig(revolutionIndex)

        if (!revolutionConfig["click_tick_height"]) {
          continue
        }

        /*
          This prevents from drawing an extra tick in place where zero
          point sign should be drawn.
        */
        let clickIndex = revolutionIndex === 0 ? 1 : 0

        for (clickIndex; clickIndex < this.clicksPerRotation; clickIndex++) {
          const coordinateX = this.distanceBetweenClicks * clickIndex
          const [leftCoordinateX, rightCoordinateX] = this.applyDirectionAndSplit(coordinateX)

          this.drawTick(leftCoordinateX, revolutionIndex, revolutionConfig["click_tick_height"])
          this.drawTick(rightCoordinateX, revolutionIndex, revolutionConfig["click_tick_height"])
        }
      }
    },
    drawYardageRelatedItems() {
      for(let yardageItemIndex = 0; yardageItemIndex < this.calculationResults.length; yardageItemIndex++) {

        const calculationResult = this.calculationResults[yardageItemIndex]
        const revolutionIndex = Math.floor(this.permitClickValue(calculationResult.clicks) / this.clicksPerRotation)

        if (revolutionIndex === this.numberOfRevolutions) {
          break
        }

        if (calculationResult.range % 100 === 0) {
          const numberToDraw = (calculationResult.range / 100).toFixed()
          this.drawYardageItem(yardageItemIndex, revolutionIndex, "hundred_yards", {numberToDraw: numberToDraw})
        } else if (calculationResult.range % 50 === 0) {
          this.drawYardageItem(yardageItemIndex, revolutionIndex, "half_yards")
        } else if (calculationResult.range % 25 === 0) {
          this.drawYardageItem(yardageItemIndex, revolutionIndex, "quarter_yards")
        }
      }
    },
    drawYardageItem(yardageItemIndex, revolutionIndex, type = "noyards", clickOptions = {}) {
      let clickTypeOptions = this.revolutionConfig(revolutionIndex)[type]
      const coordinateX = this.horizontalCoordinateForYardageResult(yardageItemIndex, revolutionIndex)

      if (!clickTypeOptions) {
        return
      } else if (typeof(clickTypeOptions) === "number") {
        clickTypeOptions = {style: "tick", tick_height: clickTypeOptions}
      }

      const options = {...clickTypeOptions, ...clickOptions}

      if (yardageItemIndex == 0) {
        this.drawNumber(this.zeroPointX, revolutionIndex, options)
      } else {
        this.drawYardageElementToRevolution(clickTypeOptions.style, coordinateX, revolutionIndex, options)
      }
    },
    drawYardageElementToRevolution(elementType, coordinateX, revolutionIndex, options) {
      const [firstCoordinateX, secondCoordinateX] = this.applyDirectionAndSplit(coordinateX)
      const drawFunction = this.drawFunctionByElementType(elementType)

      drawFunction.call(this, firstCoordinateX, revolutionIndex, options)
      drawFunction.call(this, secondCoordinateX, revolutionIndex, options)
    },
    drawFunctionByElementType(elementType) {
      switch(elementType) {
        case "tick":
          return this.drawTick
        case "circle":
          return this.drawCircle
        case "number":
          return this.drawNumber
        case "tick_with_number":
          return this.drawTickWithNumber
      }
    },
    drawTick(tickX, revolutionIndex, options) {
      const tickHeight = (typeof(options) === "object" ? options["tick_height"] : options)
      const [revolutionTopY, revolutionBottomY] = this.revolutionY(revolutionIndex)
      const revolutionHeight = revolutionBottomY - revolutionTopY

      let tickY1 = revolutionBottomY
      let tickY2 = revolutionBottomY - countPercentage(revolutionHeight, tickHeight)

      if (this.revolutionConfig(revolutionIndex).drawFromTopToBottom) {
        tickY1 = revolutionTopY
        tickY2 = tickY1 + countPercentage(revolutionHeight, tickHeight)
      }

      this.svg
        .line(tickX, tickY1, tickX, tickY2)
        .stroke({color: this.itemsFillColor, width: 1})
    },
    drawCircle(circleX, revolutionIndex, options) {
      const circleY = this.revolutionYCenter(revolutionIndex)

      this.svg
        .circle(1)
        .attr({fill: this.itemsFillColor, cx: circleX, cy: circleY})
    },
    drawNumber(numberX, revolutionIndex, options) {
      const revolutionHeight = this.revolutionHeight(revolutionIndex)
      const [revolutionTopY, revolutionBottomY] = this.revolutionY(revolutionIndex)
      const fontSize = countPercentage(revolutionHeight, options.font_size)
      const numberY = revolutionTopY + countPercentage(revolutionHeight, options.number_offset) + fontSize

      this.drawNumberToSVG(options.numberToDraw, numberX, numberY, fontSize)
    },
    drawTickWithNumber(coordinateX, revolutionIndex, options) {
      this.drawTick(coordinateX, revolutionIndex, options)
      this.drawNumber(coordinateX, revolutionIndex, options)
    },
    drawNumberToSVG(text, x, y, fontSize, additionalAttrs = {}) {
      this.svg
        .plain(text)
        .font({anchor: "middle", family: FONT_FAMILY, size: fontSize})
        .attr({fill: this.itemsFillColor})
        .attr({x: x, y: y})
        .attr(additionalAttrs)
    },
    drawZeroPoint(options) {
      const revolutionIndex = 0
      const [revolutionTopY, revolutionBottomY] = this.revolutionY(revolutionIndex)
      const revolutionHeight = revolutionBottomY - revolutionTopY

      const fontSize = countPercentage(revolutionHeight, ZERO_POINT_FONT_SIZE)
      const zeroPointY = revolutionBottomY - countPercentage(revolutionHeight, ZERO_POINT_TICK_HIEGHT) - 1 // 1px

      this.drawNumberToSVG(0, this.zeroPointX, zeroPointY, fontSize)
      this.drawTick(this.zeroPointX, revolutionIndex, ZERO_POINT_TICK_HIEGHT)
    },
    horizontalCoordinateForYardageResult(yardageResultIndex, revolutionIndex) {
      const clickValue = this.permitClickValue(this.calculationResults[yardageResultIndex].clicks)
      return this.distanceBetweenClicks * clickValue - (this.widthInPx * revolutionIndex)
    },
    applyDirectionAndSplit(coordinateX) {
      if (this.direction === "counter_clockwise") {
        coordinateX = this.widthInPx - coordinateX
      }

      return [coordinateX - this.halfWidthPx, coordinateX + this.halfWidthPx]
    },
    permitClickValue(clickValue) {
      let permitted = clickValue < 0 ? 0 : clickValue

      if (this.snapToClicks) {
        /*
          When we need to snap yardage signs to actual clicks we
          use ceil function to make sure that a bullet will actually
          reach that yardage. If we were using round we would have
          cases where permitted is rounded to the lowest integer,
          hence a bullet would fly fewer yards than a yardage sign says.
        */
        permitted = Math.ceil(permitted)
      }

      return permitted
    },
  },
  mounted() {
    // Initialize SVG object
    this.svg = SVG(this.$refs.labelPreview)
    this.draw()
  },
  watch: {
    $props: {
      handler() {
        this.redraw()
      },
      deep: true
    }
  }
}
</script>
